<template>
  <!-- App -->
  <div
    id="app"
    class="w-screen h-screen flex transition-colors duration-1000"
    :class="`${getTheme}-background `"
  >
    <!-- Left Navigation -->
    <Sidebar 
      v-if="isUserLoggedIn && !isRoutePublic && !hideSidebar"
      class="flex-shrink-0"
    />

    <!-- Settings Drawer -->
    <SettingsDrawer
      v-if="isUserLoggedIn && getSettingsDrawerViewMode"
      @close="closeSettingsDrawer"
    />

    <SubscriptionDrawer
      v-if="isUserLoggedIn && getSubscriptionDrawerViewMode"
      :initial-interval="getSubscriptionDrawerInterval"
      @close="closeSubscriptionDrawer"
    />

    <!-- Briefs Submissions -->
    <!-- <UploadSidebar v-if="this.$route.name === 'BriefPublicView'" /> -->

    <!-- Main Routes -->
    <router-view 
      :key="$route.fullPath"
      class="flex-grow"
    />

    <!-- Guided setup walkthroughs (survives route changes) -->
    <TourOverlay v-if="isUserLoggedIn" />

    <!-- Base Notifications -->
    <BaseNotifyAlert />
    <BaseNotifyPlatformChange />
  </div>
</template>

<script>
import Sidebar from './components/globals/Sidebar'
import SettingsDrawer from './components/settings/SettingsDrawer.vue'
import SubscriptionDrawer from './components/account/subscription/SubscriptionDrawer.vue'
import UploadSidebar from './components/globals/UploadSidebar'
import TourOverlay from './components/tours/TourOverlay.vue'
import { mapGetters, mapMutations } from 'vuex'

export default {
  components: { Sidebar, SettingsDrawer, UploadSidebar, SubscriptionDrawer, TourOverlay },
  computed: {
    ...mapGetters('AuthModule', ['isUserLoggedIn', 'getUser']),
    ...mapGetters('MiscModule', ['getSettingsDrawerViewMode', 'getSubscriptionDrawerViewMode', 'getSubscriptionDrawerInterval', 'getTheme']),
    ...mapGetters('RouterModule', ['getCurrentRouteId']),
    hideSidebar () {
      return this.$route.meta.hideSidebar
    },
    isRoutePublic () {
      return !this.$route.meta.requiresAuth
    }
  },
  watch: {
    // We use a watcher here since we need to wait for the router to finish initializing
    // If there's a settings query, we'll set the settings drawer view mode to trigger the drawer to open
    '$route.query.settings': {
      handler (settingsView) {
        const validSettingsViews = ['account', 'team', 'whitelabel', 'referral', 'integrations', 'plan', 'billing', 'notifications', 'lens', 'expert']
        if (validSettingsViews.includes(settingsView)) {
          this.SET_SETTINGS_DRAWER_VIEW_MODE(settingsView)

          if (settingsView === 'lens') {
            const params = new URLSearchParams(window.location.href)
            console.log('Setting current route id', params.get('lensId'))

            this.SET_CURRENT_ROUTE_ID(params.get('lensId'))
          }
        }
      },
      immediate: true
    },
    '$route.query.subscription': {
      handler (manageView) {
        if (manageView === 'manage') {
          this.SET_SUBSCRIPTION_DRAWER_VIEW_MODE({state: true})
        }
      }
    }
  },
  beforeMount () {
    // Initialize Featurebase SDK
    const script = document.createElement("script");
    script.src = "https://do.featurebase.app/js/sdk.js";
    script.id = "featurebase-sdk";
    document.head.appendChild(script);
  },
  mounted () {
    // Set Featurebase Config
    const win = window;
    if (typeof win.Featurebase !== "function") {
      win.Featurebase = function () {
        (win.Featurebase.q = win.Featurebase.q || []).push(arguments);
      };
    }
    win.Featurebase(
      "init_changelog_widget", 
      {
        organization: "foreplay", // Replace this with your organization name, copy-paste the subdomain part from your Featurebase workspace url (e.g. https://*yourorg*.featurebase.app)
        popup: {
          enabled: true, // Add this to enable the popup view of the changelog
          usersName: this.getUser?.name ?? undefined, // This will show the user's name in the popup as a greeting
          autoOpenForNewUpdates: false, // This will open the popup for new updates
        },
        theme: "dark", // Choose between dark or light theme
        locale: "en", // Change the language, view all available languages from https://help.featurebase.app/en/articles/8879098-using-featurebase-in-my-language
      },
      (_, data) => {
        if (data?.action === 'unreadChangelogsCountChanged') {
          this.SET_UNREAD_CHANGELOG_COUNT(data.unreadCount)
        }
      }
    );
  },
  beforeMount () {
    // Initialize Featurebase SDK
    const script = document.createElement("script");
    script.src = "https://do.featurebase.app/js/sdk.js";
    script.id = "featurebase-sdk";
    document.head.appendChild(script);
  },
  mounted () {
    // Set Featurebase Config
    const win = window;
    if (typeof win.Featurebase !== "function") {
      win.Featurebase = function () {
        (win.Featurebase.q = win.Featurebase.q || []).push(arguments);
      };
    }
    win.Featurebase(
      "init_changelog_widget", 
      {
        organization: "foreplay", // Replace this with your organization name, copy-paste the subdomain part from your Featurebase workspace url (e.g. https://*yourorg*.featurebase.app)
        popup: {
          enabled: true, // Add this to enable the popup view of the changelog
          usersName: this.getUser?.name ?? undefined, // This will show the user's name in the popup as a greeting
          autoOpenForNewUpdates: false, // This will open the popup for new updates
        },
        theme: "dark", // Choose between dark or light theme
        locale: "en", // Change the language, view all available languages from https://help.featurebase.app/en/articles/8879098-using-featurebase-in-my-language
      },
      (_, data) => {
        if (data?.action === 'unreadChangelogsCountChanged') {
          this.SET_UNREAD_CHANGELOG_COUNT(data.unreadCount)
        }
      }
    );
  },
  methods: {
    ...mapMutations('MiscModule', ['SET_SETTINGS_DRAWER_VIEW_MODE', 'SET_UNREAD_CHANGELOG_COUNT', 'SET_SUBSCRIPTION_DRAWER_VIEW_MODE']),
    ...mapMutations('RouterModule', ['SET_CURRENT_ROUTE_ID']),
    closeSubscriptionDrawer () {
      this.SET_SUBSCRIPTION_DRAWER_VIEW_MODE({state: null})
    },
    closeSettingsDrawer () {
      this.SET_SETTINGS_DRAWER_VIEW_MODE(null)
      this.SET_CURRENT_ROUTE_ID(null)
    }
  }
}
</script>

<style lang="sass" scoped>
#app
  font-family: "Inter var", "system-ui"
  font-size: 15px
  background: linear-gradient(0deg, rgba(219, 222, 241, 0.04) 0%, rgba(219, 222, 241, 0.04) 100%), #060710
  @apply flex h-full min-h-screen w-screen

// Disabled until we want to make the app bg dynamic:

// .green-background
//   background-color: #061B16

// .blue-background
//   background-color: #06081B

// .purple-background
//   background-color: #0F061B

// .red-background
//   background-color: #0e080c

</style>
